/**
 * Korpus Builder v2 — typy parametrického korpusu (F1).
 *
 * Terminológia potvrdená z dielne (07/2026): „vložený/naložený" sa
 * vzťahuje na PLÁT:
 * - vložený plát  → plát a dno MEDZI bokmi, bok = plná výška,
 * - naložený plát → plát a dno CEZ boky,    bok = výška − 2t.
 * Iné typy neexistujú (polonaložený odstránený 07/2026).
 */

export type TypKonstrukcie = "vlozeny_plat" | "nalozeny_plat";

/**
 * Typ chrbta — overené reálnymi TopSolid exportmi (07/2026):
 * - hdf: 4 mm v drážke 8 mm/strana (drazka_7mm operácia),
 * - ltd: VLOŽENÝ plnohodnotný chrbát (V−2t × Š−2t × t), police −(t+2) mm
 *   (pri 18 mm −20),
 * - sololit: pribíjaný 4 mm, (V−10) × (Š−10), bez CNC, police −6 mm.
 */
export type ChrbatTyp = "hdf" | "ltd" | "sololit" | "none";

/** Referencia pozície medzisteny — os medzisteny v mm od hrany otvoru. */
export type DelenieRef =
  | "stred"
  | "od_dna"
  | "od_stropu"
  | "od_boku_l"
  | "od_boku_p";

export interface Delenie {
  /** vodorovne = vodorovná medzistena (delí otvor na dolný/horný),
   *  zvislo = zvislá medzistena (delí na ľavý/pravý). */
  smer: "vodorovne" | "zvislo";
  ref: DelenieRef;
  /** Vzdialenosť osi medzisteny od referenčnej hrany otvoru (mm).
   *  Pri ref="stred" sa ignoruje. */
  mm: number;
  deti: [Otvor, Otvor];
}

/**
 * Typ čela (dvere aj čelá zásuviek) — KONSTRUKCIE.md §14:
 * - naložené: prekrýva vonkajšiu hranu t−2, medzistenu t/2−1,
 *   pohľadová plocha 20 mm pred korpusom (najpoužívanejšie),
 * - vložené: otvor −2 mm/strana, v rovine prednej hrany,
 * - polonaložené: prekrytie t/2−1 všade.
 */
export type TypCela = "nalozene" | "vlozene" | "polonalozene";

/** Dvierka na listovom otvore — rozmery sa odvodia z otvoru. */
export interface DvierkaConfig {
  kridla: 1 | 2;
  typ: TypCela;
  /** Ručný počet závesov na krídlo — null/undefined = auto podľa hmotnosti. */
  zavesy?: number | null;
}

/** Zásuvky na listovom otvore — čelá + korpusy + výsuvy z otvoru. */
export interface ZasuvkyConfig {
  pocet: number;
  /** Ručná výška boku boxu (mm) — null/undefined = auto podľa
   *  naučeného vzorca (naložené (V−240)/N, vložené V/N−40). */
  vyskaBoku?: number | null;
  /** Typ čiel — ako dvierka (KONSTRUKCIE.md §14). */
  celoTyp: TypCela;
}

export interface Otvor {
  id: string;
  delenie?: Delenie;
  /** Počet políc v otvore (len listové otvory bez delenia). */
  police: number;
  /** Dvierka na otvore (len listové otvory). */
  dvierka?: DvierkaConfig | null;
  /** Zásuvky na otvore (len listové otvory). */
  zasuvky?: ZasuvkyConfig | null;
}

export interface NohyConfig {
  enabled: boolean;
  vyskaMm: number;
  /** Vybraný produkt z kovania (set/položka). */
  material: { id: string; nazov: string; cena: number } | null;
  /** Ručný override počtu — null = auto podľa šírky. */
  pocetOverride: number | null;
}

/**
 * Presah dosky oproti korpusu (mm). Pre vodorovné dosky (plát, dno)
 * je a/b = ľavý/pravý; pre boky a/b = dole/hore.
 */
export interface Presah {
  a: number;
  b: number;
  predny: number;
  zadny: number;
}

export type DoskaId = "plat" | "dno" | "bok_l" | "bok_p";

export type Presahy = Record<DoskaId, Presah>;

export function nulovePresahy(): Presahy {
  return {
    plat: { a: 0, b: 0, predny: 0, zadny: 0 },
    dno: { a: 0, b: 0, predny: 0, zadny: 0 },
    bok_l: { a: 0, b: 0, predny: 0, zadny: 0 },
    bok_p: { a: 0, b: 0, predny: 0, zadny: 0 },
  };
}

export interface KorpusParams {
  sirka: number;
  vyska: number;
  hlbka: number;
  t: number;
  typ: TypKonstrukcie;
  chrbat: ChrbatTyp;
  nohy: NohyConfig;
  presahy: Presahy;
  /** Vybraný záves z cenníka — pre auto závesy dvierok. */
  zaves?: { id: string; nazov: string; cena: number } | null;
  /** Vybraný výsuv z cenníka — pre zásuvky (cena za pár/ks podľa cenníka). */
  vysuv?: { id: string; nazov: string; cena: number } | null;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GenDielec {
  nazov: string;
  x: number;
  y: number;
  z: number;
  ks: number;
  /** Operácia priradená dielcu (napr. drazka_7mm pri HDF chrbte). */
  operacia?: string;
  /** Smer vlákien (default X; dvierka Y). */
  smer?: "X" | "Y";
  /** CNC nadmiera +5 mm/strana — KONSTRUKCIE.md §2: boky/plát/dno/
   *  chrbát/korpus zásuvky áno; police/dvierka/čelá/sololit nie. */
  cnc: boolean;
}

export interface GenKovanie {
  nazov: string;
  materialId: string | null;
  ks: number;
  poznamka?: string;
  /** Katalógový kód z dielne (56-3550, 56-3690, 56-0248…) — builder
   *  ho dohľadá v cenníku, ak nie je ručný výber materiálu. */
  katalog?: string;
}

export interface GenVysledok {
  dielce: GenDielec[];
  kovanie: GenKovanie[];
  /** Chyby validácie (mimo rozsahu…) — pri neprázdnych sa nevytvára. */
  chyby: string[];
  /** Varovania — tvorbu neblokujú (napr. polica > 964 mm). */
  varovania: string[];
}

/** Medzera čiel (dvere naložené aj čelá zásuviek) — potvrdené 2 mm (F2). */
export const MEDZERA_CIEL = 2;

/** Pohľadová plocha naloženého/polonaloženého čela je 20 mm pred
 *  korpusom (KONSTRUKCIE.md §14) — pri hr. 18 vzniká 2 mm medzera. */
export const PREDSADENIE_CELA = 20;
